import {
    Dispatch, useCallback, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { notification } from 'ui-library';
import { ApplicationStatus, POLLING_STATUS } from 'components/DocBox/DocBox.types';
import { ApplicationTypesEnum } from 'components/ApplicationDetails/Applications/Applications.types';
import { LpzSubmissionFailedKeyword, ZurichInvestSupportEmailAddress } from 'constants/constants';
import { dateFormatZurich, formatDate } from 'utils/datetime';
import { ApplicationDetailsActionTypes } from 'datasource/useApplicationDetails/ApplicationDetailsManager.types';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import { usePollSubmissionStatus } from 'hooks/usePollSubmissionStatus';

type UseApplicationSubmissionParams = {
    dispatch: Dispatch<any>;
    setApiError: (error: { message: string } | null) => void;
    setLoadingOverlayActive: (active: boolean) => void;
    profile: any;
    showCanNotSubmitApplicationMessage: boolean;
    hasAccessToReUpload?: boolean;
    isFollowUpInvestorProfileCase?: boolean;
    canCompleteFollowUpInvestorProfileApplication?: boolean;
};

/**
 * Owns the application submission flow: the `changePlan` path and the `signDocuments` + poll path,
 * plus the `advisoryDocSubmittedForReview` flag. Extracted verbatim from ApplicationDetailsManager
 * (dep arrays preserved).
 */
export const useApplicationSubmission = ({
    dispatch,
    setApiError,
    setLoadingOverlayActive,
    profile,
    showCanNotSubmitApplicationMessage,
    hasAccessToReUpload,
    isFollowUpInvestorProfileCase,
    canCompleteFollowUpInvestorProfileApplication,
}: UseApplicationSubmissionParams) => {
    const { t } = useTranslation();
    const { mutateAsync: handleMutation } = useGenericAPIMutation();
    const { poll: pollSubmissionStatus } = usePollSubmissionStatus();
    const queryClient = useQueryClient();

    const [advisoryDocSubmittedForReview, setAdvisoryDocSubmittedForReview] = useState(false);

    const lpzSubmissionNotSuccessful = useMemo(() => t('LPZ.Submission.Not.Successful', { email: ZurichInvestSupportEmailAddress }), [t]);

    const dispatchSubmitted = useCallback((applicationId: number, allowComplete: boolean) => {
        queryClient.invalidateQueries({ queryKey: ['getInvestmentAppsByContainerId'] });

        if (showCanNotSubmitApplicationMessage && !hasAccessToReUpload) {
            dispatch({
                type: ApplicationDetailsActionTypes.SUBMIT_APPLICATION,
                payload: {
                    applicationId,
                    status: ApplicationStatus.advisoryInReview,
                    submitted: null,
                    submittedBy: null,
                    isSubmissionReviewPending: true,
                },
            });
            setAdvisoryDocSubmittedForReview(true);
        } else {
            const agent = `${profile?.FirstName} ${profile?.LastName}`;

            dispatch({
                type: ApplicationDetailsActionTypes.SUBMIT_APPLICATION,
                payload: {
                    applicationId,
                    status: (allowComplete && isFollowUpInvestorProfileCase && canCompleteFollowUpInvestorProfileApplication)
                        ? ApplicationStatus.completed : ApplicationStatus.submitted,
                    submitted: formatDate(new Date(), dateFormatZurich),
                    submittedBy: agent,
                    isSubmissionReviewPending: false,
                },
            });
        }
    }, [showCanNotSubmitApplicationMessage, hasAccessToReUpload, profile, isFollowUpInvestorProfileCase, canCompleteFollowUpInvestorProfileApplication, queryClient]);

    const submitApplication = useCallback(async (payload: { applicationId: number; planType: string, productExternalId?: string }) => {
        setLoadingOverlayActive(true);

        if (payload?.planType === ApplicationTypesEnum.CHANGE || payload?.planType === ApplicationTypesEnum.NEW_EP || payload?.planType === ApplicationTypesEnum.CHANGE_EP) {
            handleMutation({
                service: 'customInvestmentService',
                API: 'changePlan',
                payload: { investmentApplicationId: payload.applicationId, isNewFlow: true },
            }).then((res: any) => {
                if (res?.status === 201) {
                    dispatchSubmitted(payload.applicationId, false);
                }
                setLoadingOverlayActive(false);
            }).catch(() => {
                // error notification is shown by useGenericAPIMutation's onError
                setLoadingOverlayActive(false);
            });

            return null;
        }

        try {
            const signResponse = await handleMutation({
                service: 'customInvestmentService',
                API: 'signDocuments',
                payload: { investmentApplicationId: payload.applicationId, isNewFlow: true },
            });
            const identifier = (signResponse as any)?.data;

            await pollSubmissionStatus(identifier, {
                onResult: (data) => {
                    if (data?.status === POLLING_STATUS.success) {
                        dispatchSubmitted(payload.applicationId, true);
                    } else if (data?.status === POLLING_STATUS.notFound) {
                        setApiError({ message: JSON.parse(data?.jsonResult as string)?.Error });
                        notification.open({ content: JSON.parse(data?.jsonResult as string)?.Error, type: 'error' });
                    } else if (data?.status === POLLING_STATUS.error) {
                        const errorMessage = data?.jsonResult ? JSON.parse(data?.jsonResult)?.Error || t('clientDashboard.application.completionFailed') : t('clientDashboard.application.completionFailed');

                        if (errorMessage && errorMessage.includes(LpzSubmissionFailedKeyword)) {
                            setApiError({ message: lpzSubmissionNotSuccessful });
                            notification.open({ content: lpzSubmissionNotSuccessful, type: 'error' });
                        } else {
                            setApiError({ message: errorMessage });
                            notification.open({ content: errorMessage, type: 'error' });
                        }
                    }
                },
                onHttp404: (body) => {
                    setApiError({ message: body as string });
                    notification.open({ content: body as string, type: 'error' });
                },
                onGiveUp: () => {
                    notification.open({ content: t('clientDashboard.application.completionFailed'), type: 'error' });
                },
            });
        } finally {
            setLoadingOverlayActive(false);
        }

        return undefined;
    }, [handleMutation, pollSubmissionStatus, dispatchSubmitted, setLoadingOverlayActive, setApiError, t, lpzSubmissionNotSuccessful]);

    return { submitApplication, advisoryDocSubmittedForReview, setAdvisoryDocSubmittedForReview };
};
